import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Upload, FileText, Download, Eye, Sparkles, Loader2, Search, Trash2,
  RefreshCcw, Edit2, AlertTriangle, CheckCircle, Clock, UserCheck, Filter, X
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const MESI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const STATUS_MAP = {
  new: { label: 'Nuova', icon: Clock, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  viewed: { label: 'Visualizzata', icon: Eye, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  downloaded: { label: 'Scaricata', icon: Download, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export const BustePaga = () => {
  const { user, isAdmin } = useAuth();
  return isAdmin ? <AdminView /> : <EmployeeView />;
};

// ========== ADMIN VIEW ==========
const AdminView = () => {
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [confirmDoc, setConfirmDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('busta_paga');
  const [filters, setFilters] = useState({ dipendente: '', anno: '', mese: '', stato: '' });
  const [searchQ, setSearchQ] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dipendente) params.set('dipendente_id', filters.dipendente);
      if (filters.anno) params.set('anno', filters.anno);
      if (filters.mese) params.set('mese', filters.mese);
      if (filters.stato) params.set('stato', filters.stato);
      
      const dRes = await api.get(`/hr/buste-paga?${params}`);
      setDocs(dRes.data);
      
      const [uRes, sRes] = await Promise.all([
        api.get('/users'), api.get('/hr/buste-paga/stats')
      ]);
      setUsers(uRes.data); setStats(sRes.data);
    } catch (err) {
      console.error('Errore caricamento:', err);
      toast.error('Errore caricamento dati');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (!loading) fetchAll(); }, [filters]);

  const filtered = useMemo(() => {
    let list = docs.filter(d => (d.document_type || 'busta_paga') === activeTab);
    if (!searchQ) return list;
    const q = searchQ.toLowerCase();
    return list.filter(d =>
      (d.employee_name || '').toLowerCase().includes(q) ||
      (d.employee_name_extracted || '').toLowerCase().includes(q) ||
      (d.file_name || '').toLowerCase().includes(q)
    );
  }, [docs, searchQ, activeTab]);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa busta paga?')) return;
    try { await api.delete(`/hr/buste-paga/${id}`); toast.success('Eliminata'); fetchAll(); } catch {}
  };

  const handleRegenAI = async (id) => {
    toast.info('Rigenerazione AI in corso...');
    try { await api.post(`/hr/buste-paga/${id}/regenerate-ai`, {}, { timeout: 180000 }); toast.success('Riepilogo AI aggiornato'); fetchAll(); }
    catch { toast.error('Errore'); }
  };

  const openViewer = async (doc) => {
    setViewerDoc(doc); setViewerOpen(true);
    try { await api.post(`/hr/buste-paga/${doc.id}/track-view`); } catch {}
  };

  const handleDownload = async (doc) => {
    try { await api.post(`/hr/buste-paga/${doc.id}/track-download`); } catch {}
    try {
      const response = await api.get(`/hr/buste-paga/file/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `busta_paga_${doc.month}_${doc.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore durante il download');
    }
  };

  const handleAssign = async (docId, empId) => {
    try {
      await api.patch(`/hr/buste-paga/${docId}?employee_id=${empId}`);
      toast.success('Assegnata'); fetchAll();
    } catch { toast.error('Errore assegnazione'); }
  };

  const openEdit = (doc) => { setEditDoc(doc); setEditOpen(true); };

  const years = [...new Set(docs.map(d => d.year).filter(Boolean))].sort((a, b) => b - a);
  const dipendenti = users.filter(u => u.ruolo === 'dipendente');

  return (
    <div className="p-5 md:p-7 min-h-screen" data-testid="buste-paga-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Documenti Retributivi</h1>
          <p className="text-sm text-slate-500 mt-1">Buste Paga e CUD del team</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} data-testid="upload-btn">
          <Upload size={16} className="mr-2" /> Carica Documento
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-white rounded-lg p-1 border border-slate-200 w-fit">
        <button
          onClick={() => setActiveTab('busta_paga')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'busta_paga' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid="tab-buste-paga"
        >
          Buste Paga ({docs.filter(d => (d.document_type || 'busta_paga') === 'busta_paga').length})
        </button>
        <button
          onClick={() => setActiveTab('cud')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'cud' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          data-testid="tab-cud"
        >
          CUD ({docs.filter(d => d.document_type === 'cud').length})
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {[
            { label: 'Totali', value: stats.total, color: 'text-slate-900' },
            { label: 'Questo mese', value: stats.this_month, color: 'text-blue-600' },
            { label: "Quest'anno", value: stats.this_year, color: 'text-indigo-600' },
            { label: 'Visualizzate', value: stats.viewed, color: 'text-amber-600' },
            { label: 'Non viste', value: stats.not_viewed, color: 'text-red-600' },
            { label: 'Scaricate', value: stats.downloaded, color: 'text-emerald-600' },
            { label: 'Da verificare', value: stats.unassigned, color: 'text-orange-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Filter size={14} className="text-slate-400" />
        <Select value={filters.dipendente} onValueChange={v => setFilters(p => ({...p, dipendente: v === 'all' ? '' : v}))}>
          <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Dipendente" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {dipendenti.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.anno ? String(filters.anno) : ''} onValueChange={v => setFilters(p => ({...p, anno: v === 'all' ? '' : v}))}>
          <SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue placeholder="Anno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.mese ? String(filters.mese) : ''} onValueChange={v => setFilters(p => ({...p, mese: v === 'all' ? '' : v}))}>
          <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="Mese" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {MESI.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.stato} onValueChange={v => setFilters(p => ({...p, stato: v === 'all' ? '' : v}))}>
          <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="da_verificare">Da verificare</SelectItem>
            <SelectItem value="new">Non viste</SelectItem>
            <SelectItem value="viewed">Visualizzate</SelectItem>
            <SelectItem value="downloaded">Scaricate</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Cerca..." className="pl-8 h-8 text-xs" data-testid="admin-search" />
        </div>
        {(filters.dipendente || filters.anno || filters.mese || filters.stato) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500" onClick={() => setFilters({ dipendente: '', anno: '', mese: '', stato: '' })} data-testid="reset-filters">
            Resetta filtri
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b text-left">
              <th className="px-3 py-2.5 font-semibold text-slate-500">Dipendente</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500">Periodo</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500">File</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500">Caricata da</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500">Data</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500">Stato</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500">Viste</th>
              <th className="px-3 py-2.5 font-semibold text-slate-500 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(doc => {
              const st = STATUS_MAP[doc.status] || STATUS_MAP.new;
              const StIcon = st.icon;
              return (
                <tr key={doc.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${!doc.employee_id ? 'bg-amber-50/30' : ''}`} data-testid={`row-${doc.id}`}>
                  <td className="px-3 py-2.5">
                    {doc.employee_id ? (
                      <span className="font-medium text-slate-800">{doc.employee_name}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={12} className="text-amber-500" />
                        <span className="text-amber-700 font-medium">Da verificare</span>
                        {doc.employee_name_extracted && <span className="text-slate-400">({doc.employee_name_extracted})</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    {doc.month ? `${MESI[doc.month]} ${doc.year}` : doc.year || '-'}
                    {doc.document_type === 'cud' && <Badge variant="outline" className="ml-1.5 text-[8px] h-4 bg-violet-50 text-violet-600 border-violet-200">CUD</Badge>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 max-w-[150px] truncate">{doc.file_name}</td>
                  <td className="px-3 py-2.5 text-slate-500">{doc.uploaded_by_name}</td>
                  <td className="px-3 py-2.5 text-slate-400">{doc.created_at ? format(new Date(doc.created_at), 'dd/MM/yy', { locale: it }) : '-'}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant="outline" className={`text-[9px] ${st.color}`}><StIcon size={10} className="mr-0.5" />{st.label}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-slate-500">
                    {doc.view_count || 0}
                    {doc.last_viewed_at && <span className="text-slate-400 block text-[9px]">{format(new Date(doc.last_viewed_at), 'dd/MM HH:mm')}</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-0.5 justify-end">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openViewer(doc)} title="Visualizza"><Eye size={12} /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleDownload(doc)} title="Scarica"><Download size={12} /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openEdit(doc)} title="Modifica"><Edit2 size={12} /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-violet-500" onClick={() => handleRegenAI(doc.id)} title="Rigenera AI"><RefreshCcw size={12} /></Button>
                      {!doc.employee_id && (
                        <Select onValueChange={v => handleAssign(doc.id, v)}>
                          <SelectTrigger className="h-6 w-[90px] text-[9px] border-amber-300"><SelectValue placeholder="Assegna" /></SelectTrigger>
                          <SelectContent>{dipendenti.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => handleDelete(doc.id)} title="Elimina"><Trash2 size={12} /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">Nessuna busta paga trovata</div>}
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} users={dipendenti} onDone={fetchAll} />
      <EditDialog open={editOpen} onOpenChange={setEditOpen} doc={editDoc} users={dipendenti} onDone={fetchAll} />
      <ViewerDialog open={viewerOpen} onOpenChange={setViewerOpen} doc={viewerDoc} />
    </div>
  );
};

// ========== EMPLOYEE VIEW ==========
const EmployeeView = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [filterAnno, setFilterAnno] = useState('');
  const [filterMese, setFilterMese] = useState('');
  const [searchQ, setSearchQ] = useState('');

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try { const r = await api.get('/hr/buste-paga'); setDocs(r.data); }
    catch {} finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return docs.filter(d => {
      if (filterAnno && d.year !== parseInt(filterAnno)) return false;
      if (filterMese && d.month !== parseInt(filterMese)) return false;
      if (searchQ && !(d.file_name || '').toLowerCase().includes(searchQ.toLowerCase())) return false;
      return true;
    });
  }, [docs, filterAnno, filterMese, searchQ]);

  const openViewer = async (doc) => {
    setViewerDoc(doc); setViewerOpen(true);
    try { await api.post(`/hr/buste-paga/${doc.id}/track-view`); } catch {}
  };

  const handleDownload = async (doc) => {
    try { await api.post(`/hr/buste-paga/${doc.id}/track-download`); } catch {}
    try {
      const response = await api.get(`/hr/buste-paga/file/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `busta_paga_${doc.month}_${doc.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore durante il download');
    }
  };

  const years = [...new Set(docs.map(d => d.year).filter(Boolean))].sort((a, b) => b - a);

  return (
    <div className="p-5 md:p-7 min-h-screen" data-testid="buste-paga-employee">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">I Miei Documenti</h1>
        <p className="text-sm text-slate-500 mt-1">{docs.length} document{docs.length === 1 ? 'o' : 'i'} disponibil{docs.length === 1 ? 'e' : 'i'}</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <Select value={filterAnno} onValueChange={v => setFilterAnno(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue placeholder="Anno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMese} onValueChange={v => setFilterMese(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="Mese" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {MESI.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Cerca..." className="pl-8 h-8 text-xs" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border text-center py-16">
          <FileText size={32} className="text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nessun documento disponibile</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(doc => {
            const st = STATUS_MAP[doc.status] || STATUS_MAP.new;
            const StIcon = st.icon;
            return (
              <div key={doc.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`emp-doc-${doc.id}`}>
                <div className="p-4 pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <FileText size={18} className="text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">
                          {doc.document_type === 'cud' ? 'CUD' : (doc.month ? MESI[doc.month] : '')} {doc.year}
                        </h3>
                        <p className="text-[10px] text-slate-400">{doc.file_name}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[9px] ${st.color}`}><StIcon size={10} className="mr-0.5" />{st.label}</Badge>
                  </div>
                  <div className="flex gap-3 text-xs mb-3">
                    {doc.importo_netto && <span className="font-semibold text-emerald-600">Netto: {doc.importo_netto.toLocaleString('it-IT', {style:'currency', currency:'EUR'})}</span>}
                    {doc.importo_lordo && <span className="text-slate-500">Lordo: {doc.importo_lordo.toLocaleString('it-IT', {style:'currency', currency:'EUR'})}</span>}
                  </div>

                  {doc.ai_summary && (
                    <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg mb-3">
                      <p className="text-[10px] font-semibold text-violet-700 flex items-center gap-1 mb-1"><Sparkles size={11} /> Riepilogo AI</p>
                      <pre className="whitespace-pre-wrap font-sans text-[11px] text-violet-600 leading-relaxed">{doc.ai_summary}</pre>
                      <p className="text-[9px] text-violet-400 mt-2 italic">Il riepilogo AI è solo informativo. Fa fede il documento PDF originale.</p>
                    </div>
                  )}

                  <p className="text-[10px] text-slate-400">Caricata il {doc.created_at ? format(new Date(doc.created_at), 'dd/MM/yyyy', { locale: it }) : '-'}</p>
                </div>
                <div className="flex border-t border-slate-100">
                  <button onClick={() => openViewer(doc)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors" data-testid={`view-${doc.id}`}>
                    <Eye size={14} /> Visualizza PDF
                  </button>
                  <div className="w-px bg-slate-100" />
                  <button onClick={() => handleDownload(doc)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 transition-colors" data-testid={`download-${doc.id}`}>
                    <Download size={14} /> Scarica PDF
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ViewerDialog open={viewerOpen} onOpenChange={setViewerOpen} doc={viewerDoc} />
    </div>
  );
};

// ========== UPLOAD DIALOG ==========
const UploadDialog = ({ open, onOpenChange, users, onDone }) => {
  const [files, setFiles] = useState([]);
  const [mode, setMode] = useState('auto');
  const [manualEmpId, setManualEmpId] = useState('');
  const [manualMonth, setManualMonth] = useState('');
  const [manualYear, setManualYear] = useState(String(new Date().getFullYear()));
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [docType, setDocType] = useState('busta_paga');
  const fileRef = useRef(null);

  const reset = () => { setFiles([]); setResults([]); setMode('auto'); setManualEmpId(''); setManualMonth(''); setDocType('busta_paga'); };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    const newResults = [];

    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('document_type', mode === 'auto' ? '' : docType);
        if (mode === 'manual' && manualEmpId) fd.append('dipendente_id', manualEmpId);
        if (mode === 'manual' && manualMonth) fd.append('mese', manualMonth);
        if (mode === 'manual' && manualYear) fd.append('anno', manualYear);
        const r = await api.post('/hr/buste-paga/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
        newResults.push({ file: file.name, success: true, data: r.data });
      } catch (e) {
        newResults.push({ file: file.name, success: false, error: e.response?.data?.detail || 'Errore' });
      }
    }

    setResults(newResults);
    setUploading(false);
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Carica Documento</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Document Type */}
          <div>
            <Label className="text-xs mb-1.5 block">Tipo Documento</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={docType === 'busta_paga' ? 'default' : 'outline'} onClick={() => setDocType('busta_paga')} className="flex-1 text-xs" data-testid="doctype-busta">
                Busta Paga
              </Button>
              <Button size="sm" variant={docType === 'cud' ? 'default' : 'outline'} onClick={() => setDocType('cud')} className="flex-1 text-xs" data-testid="doctype-cud">
                CUD
              </Button>
            </div>
          </div>

          {/* Mode Selector */}
          <div className="flex gap-2">
            <Button size="sm" variant={mode === 'auto' ? 'default' : 'outline'} onClick={() => setMode('auto')} className="flex-1 text-xs" data-testid="mode-auto">
              <Sparkles size={13} className="mr-1" /> Automatico (AI)
            </Button>
            <Button size="sm" variant={mode === 'manual' ? 'default' : 'outline'} onClick={() => setMode('manual')} className="flex-1 text-xs" data-testid="mode-manual">
              <Edit2 size={13} className="mr-1" /> Manuale
            </Button>
          </div>

          {mode === 'auto' && (
            <p className="text-[11px] text-slate-500 bg-blue-50 p-2.5 rounded-lg">
              L'AI analizzerà il documento per identificare automaticamente il dipendente, il periodo e il tipo (Busta Paga o CUD). Se non riesce a riconoscere il dipendente, il documento andrà in "Da verificare".
            </p>
          )}

          {mode === 'manual' && (
            <div className="space-y-2.5">
              <div>
                <Label className="text-xs">Dipendente</Label>
                <Select value={manualEmpId} onValueChange={setManualEmpId}>
                  <SelectTrigger data-testid="manual-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Mese</Label>
                  <Select value={manualMonth} onValueChange={setManualMonth}>
                    <SelectTrigger><SelectValue placeholder="Mese" /></SelectTrigger>
                    <SelectContent>{MESI.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Anno</Label>
                  <Input type="number" value={manualYear} onChange={e => setManualYear(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* File Drop */}
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-blue-300 transition-colors cursor-pointer"
               onClick={() => fileRef.current?.click()}>
            <Upload size={24} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Clicca o trascina i PDF qui</p>
            <p className="text-[10px] text-slate-400 mt-1">Supporta upload multiplo</p>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple className="hidden"
              onChange={e => setFiles(Array.from(e.target.files || []))} data-testid="file-input" />
          </div>

          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded text-xs">
                  <FileText size={12} className="text-slate-400" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                </div>
              ))}
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-1.5">
              {results.map((r, i) => (
                <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${r.success ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  {r.success ? <CheckCircle size={14} className="text-emerald-500 mt-0.5" /> : <AlertTriangle size={14} className="text-red-500 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{r.file}</p>
                    {r.success && r.data.employee_id && <p className="text-emerald-600">Assegnata a {r.data.employee_name}</p>}
                    {r.success && !r.data.employee_id && (
                      <p className="text-amber-600">Non riconosciuta - da verificare{r.data.employee_name_extracted ? ` (nome trovato: "${r.data.employee_name_extracted}")` : ''}</p>
                    )}
                    {!r.success && <p className="text-red-600">{r.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button onClick={handleUpload} className="w-full" disabled={uploading || files.length === 0} data-testid="confirm-upload">
            {uploading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analisi AI in corso...</>
              : <><Upload size={14} className="mr-2" /> Carica {files.length} file</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ========== EDIT DIALOG ==========
const EditDialog = ({ open, onOpenChange, doc, users, onDone }) => {
  const [empId, setEmpId] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [notes, setNotes] = useState('');
  const [replacing, setReplacing] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (doc) {
      setEmpId(doc.employee_id || '');
      setMonth(doc.month ? String(doc.month) : '');
      setYear(doc.year ? String(doc.year) : '');
      setNotes(doc.internal_notes || '');
    }
  }, [doc]);

  const handleSave = async () => {
    try {
      const params = new URLSearchParams();
      if (empId) params.set('employee_id', empId);
      if (month) params.set('month', month);
      if (year) params.set('year', year);
      params.set('internal_notes', notes);
      await api.patch(`/hr/buste-paga/${doc.id}?${params}`);
      toast.success('Aggiornata'); onOpenChange(false); onDone();
    } catch { toast.error('Errore'); }
  };

  const handleReplace = async (file) => {
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/hr/buste-paga/${doc.id}/replace`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      toast.success('PDF sostituito e AI rigenerata'); onOpenChange(false); onDone();
    } catch { toast.error('Errore sostituzione'); }
    finally { setReplacing(false); }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifica Busta Paga</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs">Dipendente</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger><SelectValue placeholder="Assegna dipendente" /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Mese</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger><SelectValue placeholder="Mese" /></SelectTrigger>
                <SelectContent>{MESI.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Anno</Label><Input type="number" value={year} onChange={e => setYear(e.target.value)} /></div>
          </div>
          <div>
            <Label className="text-xs">Note amministrative (non visibili al dipendente)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Note interne..." />
          </div>

          <div className="border-t border-slate-100 pt-3">
            <Label className="text-xs text-slate-500">Sostituisci PDF</Label>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleReplace(e.target.files[0]); }} />
            <Button size="sm" variant="outline" className="w-full mt-1 text-xs" onClick={() => fileRef.current?.click()} disabled={replacing}>
              {replacing ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCcw size={12} className="mr-1" />}
              {replacing ? 'Sostituzione in corso...' : 'Seleziona nuovo PDF'}
            </Button>
          </div>

          <Button onClick={handleSave} className="w-full" data-testid="save-edit">Salva Modifiche</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ========== PDF VIEWER DIALOG ==========
const ViewerDialog = ({ open, onOpenChange, doc }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && doc) {
      setLoading(true);
      setError(null);
      // Fetch file as blob to bypass iframe auth issue
      api.get(`/hr/buste-paga/file/${doc.id}`, { responseType: 'blob' })
        .then(response => {
          const url = URL.createObjectURL(response.data);
          setBlobUrl(url);
        })
        .catch(err => {
          setError('Impossibile caricare il file');
          console.error('Error loading PDF:', err);
        })
        .finally(() => setLoading(false));
    }
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [open, doc]);

  const handleDownload = async () => {
    if (!doc) return;
    try {
      const response = await api.get(`/hr/buste-paga/file/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `busta_paga_${doc.month}_${doc.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore durante il download');
    }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setBlobUrl(null); setError(null); } onOpenChange(v); }}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{doc.employee_name || doc.employee_name_extracted || 'Busta Paga'}</h3>
            <p className="text-[10px] text-slate-400">{doc.month ? `${MESI[doc.month]} ${doc.year}` : doc.file_name}</p>
          </div>
          <Button size="sm" variant="outline" className="text-xs" onClick={handleDownload}>
            <Download size={13} className="mr-1" /> Scarica
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-[calc(85vh-56px)]">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-[calc(85vh-56px)] text-red-500">{error}</div>
        ) : blobUrl ? (
          <iframe src={blobUrl} className="w-full border-0" style={{ height: 'calc(85vh - 56px)' }} title="Busta Paga" data-testid="pdf-viewer" />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
